import styled from 'styled-components';
import { text2Bold, text2 } from '@ardoq/typography';
import { JSX } from 'react';
import {
  EMPTY,
  FilterInfoOperator,
  FormattingOperator,
  FormattingRuleOperator,
} from '@ardoq/api-types';
import { type I18nLocale, t } from '@ardoq/i18n';

const NormalText = styled.span`
  ${text2};
`;

const BoldText = styled.span`
  ${text2Bold};
`;

type DescriptionProps = {
  fieldName: string;
  comparatorText: string;
  value: string | JSX.Element;
  valueBold?: boolean;
};
const Description = ({
  fieldName,
  comparatorText,
  value,
  valueBold = true,
}: DescriptionProps) => (
  <>
    <NormalText>{fieldName} </NormalText>
    {comparatorText && <NormalText>{comparatorText} </NormalText>}
    {valueBold ? <BoldText>{value}</BoldText> : value}
  </>
);

type IsEmptyDescriptionProps = {
  fieldNameWithPart: string;
  comparatorParsed: string;
};
const IsEmptyDescription = ({
  fieldNameWithPart,
  comparatorParsed,
}: IsEmptyDescriptionProps) => (
  <>
    <NormalText>{fieldNameWithPart} </NormalText>
    <BoldText>{comparatorParsed}</BoldText>
  </>
);

type BetweenDescriptionProps = {
  fieldNameWithPart: string;
  comparatorParsed: string;
  value1: string;
  value2: string;
  i18nLocale: I18nLocale;
};
const BetweenDescription = ({
  fieldNameWithPart,
  comparatorParsed,
  value1,
  value2,
  i18nLocale,
}: BetweenDescriptionProps) => (
  <>
    <NormalText>{fieldNameWithPart} </NormalText>
    <NormalText>{comparatorParsed} </NormalText>
    <BoldText>{value1}</BoldText>
    <NormalText>
      {' '}
      {t(i18nLocale, 'viewLegend:conditionalFormatting.and', 'and')}{' '}
    </NormalText>
    <BoldText>{value2}</BoldText>
  </>
);

type NotBetweenDescriptionProps = {
  fieldNameWithPart: string;
  value1: string;
  value2: string;
  i18nLocale: I18nLocale;
};
const NotBetweenDescription = ({
  fieldNameWithPart,
  value1,
  value2,
  i18nLocale,
}: NotBetweenDescriptionProps) => (
  <>
    <NormalText>{fieldNameWithPart} </NormalText>
    <NormalText>
      {t(
        i18nLocale,
        'viewLegend:conditionalFormatting.isNotFrom',
        'is not from'
      )}{' '}
    </NormalText>
    <BoldText>{value1}</BoldText>
    <NormalText>
      {' '}
      {t(i18nLocale, 'viewLegend:conditionalFormatting.to', 'to')}{' '}
    </NormalText>
    <BoldText>{value2}</BoldText>
  </>
);

// FormattingRuleOperator doesn't have a real comparator for list fields,
// so we use this pseudo-comparator to identify when a rule is for a list field
// and handle it accordingly in the description building logic
export const LIST_FIELD_PSEUDO_COMPARATOR = 'listField' as const;

type FilterDescriptionRaw = {
  fieldNameWithPart: string;
  comparator: FormattingOperator | typeof LIST_FIELD_PSEUDO_COMPARATOR | null;
  comparatorParsed: string;
  valueParsed: string;
};

// Helper to create JSX description
// Used to create legend items with rich text formatting for new conditional formatting
export const FilterDescriptionJSX = (
  i18nLocale: I18nLocale,
  filterDescriptionRaw?: FilterDescriptionRaw
) => {
  if (!filterDescriptionRaw) {
    return undefined;
  }

  const { fieldNameWithPart, comparator, comparatorParsed, valueParsed } =
    filterDescriptionRaw;
  const cleanValue = valueParsed.replace(/^"|"$/g, '');
  const isLabel = t(
    i18nLocale,
    'viewLegend:conditionalFormatting.operator.is',
    'is'
  );

  // Boolean checkbox rules render as "<field> is <checked|unchecked>". Detect
  // them by the comparator rather than string-matching the value, which is
  // already translated (e.g. 'aktiviert'/'coché') and would otherwise miss the
  // boolean case in non-English locales. NOT_BOOLEAN stays on the default path
  // so its comparatorParsed-driven "is <unchecked>" output is preserved.
  if (comparator === FilterInfoOperator.BOOLEAN) {
    return (
      <Description
        fieldName={fieldNameWithPart}
        comparatorText={isLabel}
        value={cleanValue}
      />
    );
  }
  switch (comparator) {
    case FormattingRuleOperator.COLOR_RANGE:
      return (
        <Description
          fieldName={fieldNameWithPart}
          comparatorText={isLabel}
          value={valueParsed}
        />
      );

    case LIST_FIELD_PSEUDO_COMPARATOR: {
      const displayValue =
        valueParsed === EMPTY
          ? t(
              i18nLocale,
              'viewLegend:conditionalFormatting.hasNoValue',
              'has no value'
            )
          : cleanValue;
      return (
        <Description
          fieldName={fieldNameWithPart}
          comparatorText={valueParsed !== EMPTY ? isLabel : ''}
          value={displayValue}
        />
      );
    }

    case FormattingRuleOperator.IS_EMPTY:
      return (
        <IsEmptyDescription
          fieldNameWithPart={fieldNameWithPart}
          comparatorParsed={comparatorParsed}
        />
      );

    case FormattingRuleOperator.BETWEEN: {
      const [val1, val2] = cleanValue.split(',');
      return (
        <BetweenDescription
          fieldNameWithPart={fieldNameWithPart}
          comparatorParsed={comparatorParsed}
          value1={val1}
          value2={val2}
          i18nLocale={i18nLocale}
        />
      );
    }

    case FormattingRuleOperator.NOT_BETWEEN: {
      const [val1, val2] = cleanValue.split(',');
      return (
        <NotBetweenDescription
          fieldNameWithPart={fieldNameWithPart}
          value1={val1}
          value2={val2}
          i18nLocale={i18nLocale}
        />
      );
    }

    case FormattingRuleOperator.GREATER_THAN_OR_EQUAL_TO:
    case FormattingRuleOperator.LESS_THAN_OR_EQUAL_TO:
    case FilterInfoOperator.GREATER_THAN:
    case FilterInfoOperator.LESS_THAN: {
      const partToReplace = `${isLabel} `;
      const cleanedComparator = comparatorParsed.startsWith(partToReplace)
        ? comparatorParsed.substring(partToReplace.length)
        : comparatorParsed;
      return (
        <Description
          fieldName={fieldNameWithPart}
          comparatorText={cleanedComparator}
          value={cleanValue}
        />
      );
    }

    default:
      return (
        <Description
          fieldName={fieldNameWithPart}
          comparatorText={comparatorParsed}
          value={cleanValue}
        />
      );
  }
};
